import type { ReactNode } from "react";
import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
import { Icon, type TablerIcon } from "../icon";
import type { TourAnchorProps } from "../tour-anchor";
import { cx } from "../tv";

export type Segment = { id: string; label: string; icon?: TablerIcon; count?: number; hidden?: boolean; anchor?: TourAnchorProps };

/** The view switcher (List, Board, Timeline ...): a soft track with the active segment lifted as a card. */
export function SegmentedControl({ segments, value, onChange, label, size = "md", trailing }: {
  segments: Segment[];
  value: string;
  onChange: (id: string) => void;
  label: string;
  size?: "md" | "sm";
  trailing?: ReactNode;
}) {
  return (
    <ToggleButtonGroup
      aria-label={label}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={(keys) => { const k = [...keys][0]; if (k !== undefined) onChange(String(k)); }}
      className="flex shrink-0 items-center gap-0.5 rounded-[12px] bg-hover p-[3px]"
    >
      {segments.filter((s) => !s.hidden).map((s) => (
        <ToggleButton
          key={s.id}
          id={s.id}
          {...s.anchor}
          className={({ isSelected, isFocusVisible, isHovered }) =>
            cx(
              "flex cursor-default items-center gap-1.5 whitespace-nowrap rounded-[9px] font-medium outline-none",
              "transition-[background-color,box-shadow,color] duration-(--dur-fast)",
              size === "md" ? "h-[30px] px-2.5 text-base" : "h-6 px-2 text-sm",
              isSelected ? "bg-panel-solid text-fg shadow-card" : isHovered ? "text-fg" : "text-fg-muted",
              isFocusVisible && "shadow-focus",
            )
          }
        >
          {s.icon && <Icon icon={s.icon} />}
          {s.label}
          {s.count !== undefined && <span className="tabular text-xs text-fg-subtle">{s.count}</span>}
        </ToggleButton>
      ))}
      {trailing}
    </ToggleButtonGroup>
  );
}
